import { getLocation, replace } from 'connected-react-router';
import React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Button,
  FlashMessage,
  IconArrow,
  InputCheckbox,
  SheetLayoutContent,
  SheetLayoutContentWrapper,
  SheetLayoutHeader,
} from '../../../../brenger-shared-ui';
import { useTypedSelector } from '../../../../hooks';
import { RootState, StopType } from '../../../../types';
import { getActiveLanguageSettings, helpTrans, translate } from '../../../../utils/localization';
import { required } from '../../../../utils/validation';
import { getAccountHasBusinessFlow, getAccountIsPayingByInvoice } from '../../../User/ducks';
import {
  actions,
  getDisableSubmittingTrsReason,
  getFirstAndLastFlexDates,
  getFlexDatesRange,
  getInitialDatePath,
  getQuote,
  getSelectedDateTime,
  getStopHelp,
  isLoading,
  isSubmitDisabled,
  validateSameAddress,
} from '../../ducks';
import { TransportRequestValidationModal } from '../TransportRequestValidationModal';
import { TermsOverview } from './overview';

const validation = {
  terms: value => required(value, translate('request_flow.payment.agree_terms_required')),
  terms_equipment: value => required(value, translate('form.fields.default.required_simple')),
};

export const SubStepTerms: React.FC = () => {
  const dispatch = useDispatch();
  const disabledReason = useTypedSelector(getDisableSubmittingTrsReason);
  const isButtonLoading = useTypedSelector(isLoading);
  const isSameAddress = useTypedSelector(validateSameAddress);
  const isEquipmentProvided = useTypedSelector(getStopHelp) === 'equipment_provided_by_customer';
  const location = useTypedSelector(getLocation);
  const payingByInvoice = useTypedSelector(getAccountIsPayingByInvoice);
  const hasBusinessFlow = useTypedSelector(getAccountHasBusinessFlow);
  const paysLater = payingByInvoice || hasBusinessFlow;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { terms, terms_equipment, newsletter } = useTypedSelector(state => state.generalTransport.choices || {});
  const [showFieldErrors, setShowFieldErrors] = React.useState(false);
  const error = useTypedSelector((state: RootState) => state.generalTransport.error);

  // Date - time
  let dtps = useTypedSelector(state => getSelectedDateTime(state, StopType.PICKUP));
  const flexFirstAndLast = useTypedSelector(getFirstAndLastFlexDates);
  const flexDatesRange = useTypedSelector(getFlexDatesRange);
  if (flexDatesRange.length > 0 && flexFirstAndLast) {
    dtps = flexDatesRange;
  }
  const hasDates = dtps.length > 0;
  const initialDatePath = useTypedSelector(getInitialDatePath);
  const isButtonDisabled = useTypedSelector(isSubmitDisabled) || !!disabledReason || !hasDates;

  const [showCancelled, setShowCancelled] = React.useState(false);
  React.useEffect(() => {
    dispatch(actions.setProgress(7));
    if (location.search.includes('payment_status=canceled')) {
      setShowCancelled(true);
      dispatch(replace(location.pathname));
    }
  }, []);

  React.useEffect(() => {
    dispatch(actions.setTrValidationError(isSameAddress ? 'same_address_for_pickup_delivery' : null));
  }, [isSameAddress]);

  const hasErrors = !terms || (isEquipmentProvided && !terms_equipment);

  const quote = useTypedSelector(getQuote);

  const proceed = (): void => {
    if (hasErrors) {
      setShowFieldErrors(true);
      return;
    }
    dispatch(actions.createTransportRequest());
  };

  if (!quote) {
    return (
      <SheetLayoutContentWrapper>
        <SheetLayoutContent>
          <div className="pt-[1em]">
            <FlashMessage type={'error'}>No data</FlashMessage>
          </div>
        </SheetLayoutContent>
      </SheetLayoutContentWrapper>
    );
  }
  return (
    <SheetLayoutContentWrapper>
      <SheetLayoutContent>
        {showCancelled && (
          <div className={'pt-[1em] pb-[1em]'}>
            <FlashMessage type={'error'}>
              {helpTrans.asComponent({ id: 'request_flow.payment_canceled.flow_message' })}
            </FlashMessage>
          </div>
        )}
        {disabledReason && (
          <div className="pb-[1em] pt-[1em]">
            <FlashMessage type={'error'}>{helpTrans.asComponent({ id: disabledReason })}</FlashMessage>
          </div>
        )}
        {!hasDates && (
          <div className="pb-1 pt-1">
            <Link to={initialDatePath} style={{ textDecoration: 'none' }}>
              <FlashMessage type={'error'}>
                <div
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  dangerouslySetInnerHTML={{ __html: translate('request_flow.terms.error_no_dates_selected') }}
                />
              </FlashMessage>
            </Link>
          </div>
        )}
        <SheetLayoutHeader>
          <h2 className="mb-1">
            <Translate id={'request_flow.terms.heading'} />
          </h2>
          <Translate id={'request_flow.terms.sub_heading'} />
        </SheetLayoutHeader>
        <TermsOverview />
        <h2 className="no-margin pt-[1em]">
          <Translate id="request_flow.terms.heading_accept_terms" />
        </h2>
        <div className="pt-[1em]">
          <InputCheckbox
            id={'newsletter'}
            type="checkbox"
            label={<Translate id={'request_flow.contact.newsletter'} />}
            checked={!!newsletter}
            onChange={() => dispatch(actions.setChoice({ newsletter: !newsletter }))}
          />
          <div className="pt-[0.5em]">
            <InputCheckbox
              id={'terms'}
              type="checkbox"
              label={
                <Translate
                  id={'request_flow.terms.agree_all'}
                  data={{
                    terms_link: (
                      <a
                        href={`https://www.brenger.nl/${getActiveLanguageSettings().labels.contentSite}/terms-customer`}
                        target={'_blank'}
                        rel="noreferrer"
                      >
                        <Translate id={'request_flow.delivery.general_terms'} />
                      </a>
                    ),
                    privacy_link: (
                      <a
                        href={`https://www.brenger.nl/${getActiveLanguageSettings().labels.contentSite}/terms-privacy`}
                        target={'_blank'}
                        rel="noreferrer"
                      >
                        <Translate id={'request_flow.delivery.privacy_statement'} />
                      </a>
                    ),
                    agreement_link: (
                      <a
                        href={`https://www.brenger.nl/${
                          getActiveLanguageSettings().labels.contentSite
                        }/terms-transport-agreement`}
                        target={'_blank'}
                        rel="noreferrer"
                      >
                        <Translate id={'request_flow.delivery.transport_condition'} />
                      </a>
                    ),
                  }}
                />
              }
              meta={{
                showError: showFieldErrors,
                error: validation.terms(terms),
              }}
              checked={!!terms}
              onChange={() => dispatch(actions.setChoice({ terms: !terms }))}
            />
          </div>
          {isEquipmentProvided && (
            <div className="pt-[0.5em]">
              <InputCheckbox
                id={'terms_equipment'}
                type="checkbox"
                label={<Translate id={'request_flow.terms.customer_equipment.label_both'} />}
                checked={!!terms_equipment}
                onChange={() => dispatch(actions.setChoice({ terms_equipment: !terms_equipment }))}
                meta={{
                  showError: showFieldErrors,
                  error: validation.terms_equipment(terms_equipment),
                }}
              />
            </div>
          )}
        </div>
        {error && <FlashMessage type={'error'} message={error} />}
        <div className="pt-[1em] flex justify-end">
          <Button
            extraClasses="order-overview--cta"
            buttonStyle="secondary"
            onClick={proceed}
            iconPos={'right'}
            disabled={isButtonDisabled}
            loading={isButtonLoading}
          >
            <Translate id={paysLater ? 'request_flow.terms.button_no_payment' : 'request_flow.terms.button'} />
            <IconArrow />
          </Button>
        </div>
      </SheetLayoutContent>
      <TransportRequestValidationModal />
    </SheetLayoutContentWrapper>
  );
};
